import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Lock, Check } from 'lucide-react';
import PromoCodeInput from './PromoCodeInput';
import { useAuth } from '@/contexts/AuthContext';

export default function PaywallModal({ isOpen = true, onClose, customMessage = "Flexible pricing for everyone! Choose from three access levels including financial hardship support.", returnTo = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Auto-scroll to top when modal opens
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const handleViewPricing = () => {
    setLoading(true);
    // If user is a guest (anonymous), redirect to login/signup first
    if (currentUser?.isAnonymous) {
      // Store the intended destination
      localStorage.setItem('postLoginRedirect', '/pricing');
      navigate('/login');
    } else {
      navigate('/pricing');
    }
  };

  const handleMaybeLater = () => {
    // If onClose is provided, call it and let it handle navigation
    // This allows static pages to control their own return destination
    if (onClose) {
      onClose();
      return; // Don't navigate - the onClose handler will do it
    }
    
    // Only navigate if no onClose handler was provided
    // Determine where to navigate:
    // 1. Use explicit returnTo prop if provided
    // 2. If on a module page (/module/*), go to /insights
    // 3. Otherwise, go to dashboard
    if (returnTo) {
      navigate(returnTo);
    } else if (location.pathname.startsWith('/module/')) {
      navigate('/insights');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto"
      onClick={handleMaybeLater}
    >
      <div className="min-h-screen flex items-start justify-center p-4 pt-8 sm:pt-12">
        <Card 
          className="w-full max-w-lg my-8" 
          style={{ background: 'var(--bg-card)', border: '2px solid var(--primary-blue)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <CardHeader className="text-center pb-4 sm:pb-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full mx-auto mb-3 sm:mb-4 flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
              <Lock className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: 'var(--primary-blue)' }} />
            </div>
            <CardTitle className="text-xl sm:text-2xl px-2" style={{ color: 'var(--text-primary)' }}>
              Unlock Full Access to CalmGuard
            </CardTitle>
            <CardDescription className="text-sm sm:text-base mt-2 px-2">
              {customMessage}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-5 pb-6">
          {/* Pricing Tiers */}
          <div className="text-center py-4 sm:py-5 px-3 sm:px-4 rounded-lg" style={{ background: 'rgba(59, 130, 246, 0.05)', border: '2px solid var(--primary-blue)' }}>
            <div className="space-y-2">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--primary-blue)' }}>$29.99</span>
                <span className="text-lg" style={{ color: 'var(--text-secondary)' }}>/year</span>
                <span className="text-xs sm:text-sm px-2 py-1 rounded-full" style={{ background: 'var(--primary-blue)', color: '#000000' }}>Most Popular</span>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Standard • Financial Hardship ($14.99/yr) • Supporter ($59.99/yr)
              </p>
            </div>
            <p className="text-xs sm:text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
              Annual subscription • Full access • Cancel anytime
            </p>
          </div>

          {/* Features */}
          <div className="space-y-2 sm:space-y-3">
            <h3 className="font-semibold text-base sm:text-lg" style={{ color: 'var(--text-primary)' }}>What's Included:</h3>
            <div className="space-y-1.5 sm:space-y-2">
              {[
                'Unlimited CalmGuard Plans',
                'Thought Fighting Tool with History',
                'All Self-Reflection Quizzes',
                'Complete Educational Library',
                'Boundary Scripts & Templates',
                'PDF Export for Your Plans',
                'Full Annual Access - Cancel Anytime'
              ].map((feature, idx) => (
                <div key={idx} className="flex items-start gap-2 sm:gap-3">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--primary-green)' }} />
                  <span className="text-xs sm:text-sm" style={{ color: 'var(--text-primary)' }}>{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-2 sm:space-y-3 pt-3 sm:pt-4">
            <Button
              onClick={handleViewPricing}
              disabled={loading}
              className="w-full h-10 sm:h-12 text-base sm:text-lg"
              style={{ 
                background: 'linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-green) 100%)',
                color: '#000000'
              }}
            >
              {loading ? 'Loading...' : 'View Pricing Options'}
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                onClick={handleMaybeLater}
                disabled={loading}
                className="w-full h-9 sm:h-10 text-sm sm:text-base"
              >
                Maybe Later
              </Button>
            )}
          </div>

          {/* Trust Badges */}
          <div className="text-center pt-3 sm:pt-4 border-t" style={{ borderColor: 'rgba(59, 130, 246, 0.2)' }}>
            <p className="text-[10px] sm:text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              🔒 Secure payment powered by Stripe • 💜 Financial hardship support available • 💯 30-day money-back guarantee
            </p>
          </div>

          {/* Promo Code Section */}
          <div className="pt-3 sm:pt-4 border-t" style={{ borderColor: 'rgba(59, 130, 246, 0.2)' }}>
            <PromoCodeInput onSuccess={onClose} />
          </div>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
